import { PowerSyncContext } from '@powersync/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from '../auth';
import { Sentry } from '../sentry';
import { clearCacheOwner, readCacheOwner, writeCacheOwner } from './cache-owner';
import { SupabaseConnector } from './connector';
import { powerSync } from './db';

const connector = new SupabaseConnector();

// Serialize connect/disconnect across auth transitions so a rapid
// sign-out -> sign-in can't let an earlier disconnectAndClear resolve AFTER a
// later connect and wipe the freshly-synced session. Each transition enqueues
// its op behind the previous one, so the op matching the latest auth state runs
// last and wins.
let opChain: Promise<unknown> = Promise.resolve();

type CacheStatus = { userId: string | null; phase: 'ready' | 'error' };

/**
 * Whether the local database has been proven to belong to the signed-in user.
 *
 * Consumed by `LocalCacheGate`, which withholds every data screen until this reads
 * `ready`. That gate sits inside the `(app)` group rather than here because gating
 * `children` would unmount the router mid-navigation — and only `(app)` reads local
 * SQLite (`/join` redeems over the network).
 */
export type LocalCachePhase = 'preparing' | 'ready' | 'error';

const LocalCacheContext = createContext<{ phase: LocalCachePhase; retry: () => void }>({
  phase: 'preparing',
  retry: () => {},
});

export function useLocalCache() {
  return useContext(LocalCacheContext);
}

/**
 * Bring the local database into a state that provably belongs to `userId`, before
 * anything connects to it or reads from it.
 *
 * `connect()` does NOT clear, so the clear that hands the cache from one account
 * to the next has to happen here. The recorded owner is what makes that durable:
 * if a sign-out's clear fails (or the process dies mid-sign-out), the previous
 * owner stays on disk and the next sign-in clears on the mismatch.
 *
 * Throws if the database can't be vouched for. The caller must not connect.
 */
async function prepareLocalCache(userId: string | null): Promise<void> {
  if (userId === null) {
    await powerSync.disconnectAndClear();
    await clearCacheOwner();
    return;
  }

  const owner = await readCacheOwner();
  if (owner === userId) return;

  // Any mismatch clears — including an UNOWNED cache. Nothing of this user's is lost
  // by that: the owner is recorded below, before connect() and before the gate opens
  // any screen that can write, so a database holding rows or queued writes always has
  // an owner. An unowned one is therefore either empty (first sign-in — the clear is a
  // no-op) or the residue of a storage skew, where AsyncStorage was wiped and SQLite
  // survived still holding the previous account's rows. Treating that as "unclaimed,
  // keep it" is exactly the bug this whole mechanism exists to prevent.
  await powerSync.disconnectAndClear();

  // Record the owner BEFORE connecting. connect() starts pulling rows immediately;
  // a process death between connect() and a post-hoc write would leave the stale
  // owner on disk and make the next launch clear THIS user's un-uploaded writes.
  // Dying between the clear and this write is the harmless direction — the next
  // launch just clears an already-empty database.
  await writeCacheOwner(userId);
}

/**
 * Initializes the local PowerSync database once, exposes it via context (so
 * components can use `useQuery`), and connects it to the sync service whenever
 * there is a session — disconnecting and clearing on sign-out.
 */
export function PowerSyncProvider({ children }: { children: ReactNode }) {
  const { session, status } = useAuth();
  const [ready, setReady] = useState(false);
  const [cacheStatus, setCacheStatus] = useState<CacheStatus | null>(null);
  // Not a count anyone reads — bumping it is how `retry()` re-runs the effect below.
  const [retryNonce, setRetryNonce] = useState(0);

  // Key connect/disconnect off the stable user identity, NOT the Session object:
  // `onAuthStateChange` hands a fresh Session on every TOKEN_REFRESHED (hourly,
  // plus each native foreground), and re-running connect() on each one needlessly
  // churns the live sync stream — PowerSync already refreshes its own credentials
  // via the connector's fetchCredentials.
  const userId = session?.user.id ?? null;

  useEffect(() => {
    let cancelled = false;
    powerSync.init().then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // Act only on a KNOWN session: until auth resolves, `userId` is null because auth is
    // UNKNOWN, not signed out, and acting on it would clear the un-uploaded write queue on
    // every cold start. Allowlist, per `AuthStatus`.
    if (!ready || status !== 'ready') return;

    let cancelled = false;
    opChain = opChain
      .then(async () => {
        try {
          await prepareLocalCache(userId);
        } catch (error) {
          // The cache may still hold another user's rows. Fail closed: gate the render,
          // report, and never connect on top of it. Gate BEFORE reporting, so a throwing
          // reporter costs us the report rather than the gate.
          if (!cancelled) setCacheStatus({ userId, phase: 'error' });
          Sentry.captureException(error, {
            tags: { area: 'powersync-lifecycle', outcome: 'cache-not-prepared' },
          });
          return;
        }

        if (!cancelled) setCacheStatus({ userId, phase: 'ready' });
        if (!userId) return;

        try {
          await powerSync.connect(connector);
        } catch (error) {
          // Unlike a failed clear, this is routine: offline is the common cause and
          // PowerSync retries internally. Report it, but keep rendering — gating the
          // app on a live connection would break offline cold start.
          Sentry.captureException(error, {
            tags: { area: 'powersync-lifecycle', outcome: 'connect-failed' },
          });
        }
      })
      // Last line of defence. Rejecting `opChain` is permanent: every later transition's
      // `.then` is skipped, so no sign-out would clear again and `retry()` would enqueue
      // onto a dead chain — a blank screen with a dead button. The handlers above own the
      // gate; this only has to keep the chain alive, and not throw doing it.
      .catch((error) => {
        try {
          Sentry.captureException(error, {
            tags: { area: 'powersync-lifecycle', outcome: 'lifecycle-op-failed' },
          });
        } catch {
          // Reporting is best-effort. It must never be what poisons the chain.
        }
      });

    return () => {
      cancelled = true;
    };
  }, [ready, status, userId, retryNonce]);

  const retry = useCallback(() => {
    setCacheStatus(null);
    setRetryNonce((n) => n + 1);
  }, []);

  const db = useMemo(() => powerSync, []);

  // A signed-out user reaches no data screen, so nothing is gated on their behalf —
  // `(app)` redirects them to /landing regardless.
  const phase: LocalCachePhase =
    userId === null
      ? 'ready'
      : cacheStatus?.userId === userId
        ? cacheStatus.phase
        : 'preparing';

  const cache = useMemo(() => ({ phase, retry }), [phase, retry]);

  return (
    <LocalCacheContext.Provider value={cache}>
      <PowerSyncContext.Provider value={db}>{children}</PowerSyncContext.Provider>
    </LocalCacheContext.Provider>
  );
}
